// ═══════════════════════════════════════════════════════════════════════════
// reglasDecisionDefaults — Valores default del motor de Reglas de Decisión
// ═══════════════════════════════════════════════════════════════════════════
// Cada módulo "inteligente" del CoWork (Central de Promociones, Central de
// Stock, Central de Publicidad, etc.) usa una serie de umbrales para sugerir
// acciones a partir de los datos sincronizados.
//
// Esos umbrales viven en Firestore en `catalogoReglasDecision/{moduloId}`
// (global) y opcionalmente en `empresas/{id}/reglasDecision/{moduloId}`
// (override por empresa). El hook `useReglasDecision` resuelve la cascada
// empresa → global → defaults (este archivo).
//
// Por qué tener defaults hardcoded acá:
//   1. Si la collection todavía no fue inicializada, el dashboard sigue
//      funcionando sin romper.
//   2. Documentación viva del shape esperado del doc.
//   3. Punto único para revisar qué umbrales usa cada módulo.
//
// Para agregar un módulo nuevo: definir un objeto MODULO_<NOMBRE> con su
// `parametros` y exportarlo en `REGLAS_DEFAULTS`. Agregarlo también al ABM
// (ReglasDecisionPage) como item del listado.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tipo de un parámetro:
 *   - 'ratio'      → multiplicador (ej. 1.2 = 120% del valor de referencia)
 *   - 'porcentaje' → fracción (ej. 0.10 = 10%; en UI se muestra como 10)
 *   - 'numero'     → entero/decimal sin formato
 */

// ───── Módulo: Central de Promociones ─────────────────────────────────────
export const MODULO_CENTRAL_PROMOCIONES = {
  moduloId: 'central-promociones',
  nombre: 'Central de Promociones',
  descripcion: 'Reglas para sugerir acciones sobre promos: sumarse, salir, apagar publi, mantener atento, promo propia agresiva.',
  version: 1,
  parametros: {
    coefTop: {
      valor: 1.2,
      label: 'Coef. "top" vs empresa',
      descripcion: 'Item con coef >= este valor se considera top performer. Soporta promo más agresiva.',
      tipo: 'ratio',
      ejemploRender: (v) => `>= ${v.toFixed(2)}× empresa`,
    },
    coefCola: {
      valor: 0.8,
      label: 'Coef. "cola" vs empresa',
      descripcion: 'Item con coef <= este valor se considera flojo. No merece descuento adicional.',
      tipo: 'ratio',
      ejemploRender: (v) => `<= ${v.toFixed(2)}× empresa`,
    },
    margenNetoPisoAbsoluto: {
      valor: 0.10,
      label: 'Piso absoluto de margen post-promo neto',
      descripcion: 'Margen mínimo absoluto para considerar la promo "OK" (independiente del margen empresa). Protege en empresas con rentabilidad muy baja.',
      tipo: 'porcentaje',
      ejemploRender: (v) => `${(v * 100).toFixed(0)}%`,
    },
    margenNetoPisoRelativo: {
      valor: 0.50,
      label: 'Piso relativo (% del margen empresa)',
      descripcion: 'Margen post-promo neto debe quedar >= este % del margen empresa neto. Adapta el umbral al contexto del negocio.',
      tipo: 'porcentaje',
      ejemploRender: (v) => `${(v * 100).toFixed(0)}% del empresa`,
    },
    vendeSoloRatioDescuento: {
      valor: 0.30,
      label: '"Vende solo" — % máximo ventas con descuento',
      descripcion: 'Si menos de este % de las ventas del item fueron con descuento, el item "vende solo" (no depende de promos).',
      tipo: 'porcentaje',
      ejemploRender: (v) => `< ${(v * 100).toFixed(0)}%`,
    },
    coefAcosCaroVsEmpresa: {
      valor: 1.5,
      label: 'ACOS "caro" vs empresa',
      descripcion: 'Si el ACOS del item supera este múltiplo del ACOS empresa, la publi se considera cara y se sugiere apagarla al sumarse a la promo.',
      tipo: 'ratio',
      ejemploRender: (v) => `> ${v.toFixed(2)}× ACOS empresa`,
    },
    perfilVentasUmbralAlto: {
      valor: 0.50,
      label: 'Perfil ventas — umbral "vende con X"',
      descripcion: 'Si más del N% de las ventas históricas del item usaron una palanca (publi o descuento), se considera que "vende con esa palanca".',
      tipo: 'porcentaje',
      ejemploRender: (v) => `>= ${(v * 100).toFixed(0)}%`,
    },
    perfilVentasUmbralBajo: {
      valor: 0.20,
      label: 'Perfil ventas — umbral "no necesita X"',
      descripcion: 'Si menos del N% de las ventas históricas usaron una palanca, se considera que el item no la necesita (palanca redundante).',
      tipo: 'porcentaje',
      ejemploRender: (v) => `< ${(v * 100).toFixed(0)}%`,
    },
    perfilVentasMinOrdenes: {
      valor: 5,
      label: 'Perfil ventas — mínimo de órdenes',
      descripcion: 'Cantidad mínima de órdenes históricas para considerar el perfil confiable. Por debajo se marca "pocos datos".',
      tipo: 'numero',
      ejemploRender: (v) => `>= ${v} órdenes`,
    },
    descuentoPromoPropiaDefault: {
      valor: 0.15,
      label: 'Descuento default para "Promo propia"',
      descripcion: 'Descuento que asume el motor para evaluar pubs sin promo ML como candidatas a promo propia. Si el margen post-descuento queda > umbral, se sugiere promo propia para mover la pub.',
      tipo: 'porcentaje',
      ejemploRender: (v) => `${(v * 100).toFixed(0)}%`,
    },
    coefPromoPropia: {
      valor: 1.0,
      label: 'Coef. corte "Promo propia" vs umbral',
      descripcion: 'Multiplicador del umbral OK que el margen post-descuento debe superar para sugerir Promo propia. 1.0 = igual al umbral OK, >1 = más exigente, <1 = más permisivo. Sirve para abrir/cerrar la cantidad de pubs huérfanas que aparecen como candidatas.',
      tipo: 'ratio',
      ejemploRender: (v) => `>= ${v.toFixed(2)}× umbral OK`,
    },
  },
}

// ───── Registro maestro de módulos ────────────────────────────────────────
// Agregar acá cada módulo nuevo. El ABM lo lista automáticamente.
export const REGLAS_DEFAULTS = {
  [MODULO_CENTRAL_PROMOCIONES.moduloId]: MODULO_CENTRAL_PROMOCIONES,
}

/**
 * Devuelve un objeto plano `{ paramId: valor }` con los defaults de un módulo.
 * Útil como fallback final cuando no hay docs en Firestore.
 *
 * @param {string} moduloId
 * @returns {Object<string, number>}
 */
export function getDefaultsParametros(moduloId) {
  const mod = REGLAS_DEFAULTS[moduloId]
  if (!mod) return {}
  const out = {}
  for (const [k, def] of Object.entries(mod.parametros || {})) {
    out[k] = def.valor
  }
  return out
}

/**
 * Devuelve la metadata completa de los parámetros de un módulo (label,
 * descripción, tipo, etc.). Lo usa el ABM para renderizar el form.
 *
 * @param {string} moduloId
 */
export function getMetadataModulo(moduloId) {
  return REGLAS_DEFAULTS[moduloId] || null
}

/**
 * Lista de módulos disponibles. Para el listado del ABM.
 */
export function listarModulos() {
  return Object.values(REGLAS_DEFAULTS)
}
